import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { RemirrorJSON } from 'remirror';
import { ClipObject, ClipObjectPending } from '../clips';
import { useAppDispatch } from '../../store/hooks';
import { addListener } from '../../store/listenerMiddleware';
import { clearCues, updateCue } from '../../store/CuesSlice';
import storage from '../../util/storage';
import { useEventsContext } from '../events/EventsContext';
import { EventName } from '../../services/eventTrackingServiceTypes';

export const StudioClipContext = React.createContext<{
  clip?: ClipObject;
  audio: HTMLAudioElement | undefined;
  srcFailedToLoad?: boolean;
  hasCueChanged: boolean;
  setHasCueChanged: (value: boolean) => void;
  setSrcFailedToLoad: (failed: boolean | undefined) => void;
  setAudio: (audio: HTMLAudioElement | undefined) => void;
  setClip: (clip: ClipObject | undefined) => void;
}>({
  // @ts-ignore React.createContext is expecting default state to be populated but does not make
  // sense in this scenario.
  clip: {},
  audio: undefined,
});

export interface ClipPreview extends ClipObject, ClipObjectPending {
  json: RemirrorJSON;
  isSaved: boolean;
}

export type ClipPreviewMap = Map<string, ClipPreview> | null;

export const StudioClipContextV2 = React.createContext<{
  previews: ClipPreviewMap | null;
  setPreviews: (previews: ClipPreviewMap) => void;
  resetPreviews: () => void;
  hasCueChanged: boolean;
  setHasCueChanged: (value: boolean) => void;
  addClipToPreviews: (
    clipId: string,
    clip: ClipObject | ClipObjectPending,
    json: RemirrorJSON
  ) => void;
  deleteClipFromPreviews: (clip: ClipObject) => void;
  setPreviewToSaved: (clipId: string) => void;
}>(
  // @ts-ignore React.createContext is expecting default state to be populated but is not necessary here
  {
    previews: null,
  }
);

export interface StudioClipPreviewProviderProps {
  children: React.ReactNode;
}

export const useStudioClipPreviewContext = () => useContext(StudioClipContext);
export const useStudioClipPreviewListContext = () =>
  useContext(StudioClipContextV2);

export const StudioClipProvider = StudioClipContext.Provider;
export const StudioClipProviderV2 = StudioClipContextV2.Provider;

export const StudioClipPreviewProvider = ({
  children,
}: StudioClipPreviewProviderProps) => {
  const [srcFailedToLoad, setSrcFailedToLoad] = useState<boolean>();
  const [audio, setAudio] = useState<HTMLAudioElement | undefined>();
  const [clip, setClip] = useState<ClipObject | undefined>(undefined);
  const [hasCueChanged, setHasCueChanged] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: ({ type }) =>
          type === updateCue.fulfilled.type ||
          type === clearCues.fulfilled.type,
        effect: (_, { getState }) => {
          const { activeNode } = getState().cues;
          if (activeNode?.attrs) {
            const hasAllZeroValues = Object.values(activeNode.attrs).every(
              val => val === 0
            );
            if (hasAllZeroValues) return;
          }
          setHasCueChanged(true);
        },
      })
    );

    return () => {
      // @ts-ignore - type issue with addListener + dispatch
      unsubscribe();
    };
  }, [dispatch, setHasCueChanged]);

  const studioClipPreviewContextValue = useMemo(
    () => ({
      clip,
      audio,
      srcFailedToLoad,
      hasCueChanged,
      setHasCueChanged,
      setSrcFailedToLoad,
      setAudio,
      setClip,
    }),
    [clip, audio, srcFailedToLoad, hasCueChanged]
  );

  return (
    <StudioClipContext.Provider value={studioClipPreviewContextValue}>
      {children}
    </StudioClipContext.Provider>
  );
};

const findNextTake = (previews: Map<string, ClipPreview>): number => {
  let highestTake = 0;

  previews.forEach(clip => {
    const takeMatch = clip.name.match(/TAKE (\d+)/);
    if (takeMatch) {
      const takeNumber = parseInt(takeMatch[1], 10);
      if (takeNumber > highestTake) {
        highestTake = takeNumber;
      }
    }
  });

  return highestTake + 1;
};

/**
 * @description storage key to save clip previews on refresh
 */
export const CLIP_PREVIEWS = 'CLIP_PREVIEWS';

const getStoredPreviews = () => {
  const storedPreviews = storage.getItem(CLIP_PREVIEWS);
  if (!storedPreviews) return null;

  let arr: [ClipObject] | [] = [];
  try {
    // Convert stringified arr to map
    arr = JSON.parse(storedPreviews);
  } catch (err) {
    console.warn('getStoredPreviews: Unable to parse stored preview data');
  }

  const newPreviews = new Map();
  arr.forEach(preview => {
    newPreviews.set(preview.id, preview);
  });

  return newPreviews;
};

export const StudioClipPreviewListProvider = ({
  children,
}: StudioClipPreviewProviderProps) => {
  const [previews, setPreviews] = useState<ClipPreviewMap>(null); // Not fetching stored previews until parsing issue can be ironed out - ET
  const [eventTracking] = useEventsContext();
  const [hasCueChanged, setHasCueChanged] = useState(true);

  const dispatch = useAppDispatch();

  const addClipToPreviews = (
    clipId: string,
    clip: ClipObject | ClipObjectPending,
    json: RemirrorJSON
  ) => {
    setPreviews(prevPreviews => {
      const newPreviews = prevPreviews
        ? new Map([...prevPreviews].reverse()) // Reverse to set new one at "end", to later reverse again to get newest at top
        : new Map();
      const nextTakeNumber = findNextTake(newPreviews);
      const newClipPreview = {
        ...clip,
        name: `TAKE ${nextTakeNumber}`,
        id: clipId,
        json,
        isSaved: false,
      };
      newPreviews.set(clipId, newClipPreview);

      // Rearrange map to ensure newest item is first
      const rearrangedPreviews = new Map([...newPreviews].reverse());

      // Save new previews to local storage
      const arr = Array.from(rearrangedPreviews).map(
        ([id, clipObj], _index) => ({
          ...clipObj,
          id,
          streamRequest: null, // Have to set this to null in order to stringify/parse for local storage
        })
      );
      storage.removeItem(CLIP_PREVIEWS);
      storage.setItem(CLIP_PREVIEWS, JSON.stringify(arr));
      return rearrangedPreviews;
    });

    return setHasCueChanged(false);
  };

  const setPreviewToSaved = useCallback(
    (clipId: string) =>
      setPreviews(prevPreviews => {
        const clip = prevPreviews?.get(clipId);
        const newMap = prevPreviews ? new Map(prevPreviews) : new Map();

        const updatedValue = {
          ...clip,
          isSaved: true,
        };

        newMap.set(clipId, updatedValue);
        // updatePreviewsInStorage(newMap);

        return newMap;
      }),
    []
  );

  const deleteClipFromPreviews = useCallback(
    (clipToDelete: ClipObject) => {
      setPreviews(prevPreviews => {
        const newPreviews = prevPreviews ? new Map(prevPreviews) : new Map();
        if (newPreviews.has(clipToDelete.id)) {
          newPreviews.delete(clipToDelete.id);
        }

        if (newPreviews?.size === 0) setHasCueChanged(true);

        // update local storage
        storage.removeItem(CLIP_PREVIEWS);
        const arr = Array.from(newPreviews).map(([clipId, clip], _index) => ({
          ...clip,
          id: clipId,
          streamRequest: null, // Have to set this to null in order to stringify/parse for local storage
        }));
        storage.setItem(CLIP_PREVIEWS, JSON.stringify(arr));

        eventTracking.track({
          type: EventName.ClipPreviewDelete,
        });

        return newPreviews;
      });
    },
    [eventTracking]
  );

  const resetPreviews = () => {
    setPreviews(null);
    storage.removeItem(CLIP_PREVIEWS);
  };

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: ({ type }) =>
          type === updateCue.fulfilled.type ||
          type === clearCues.fulfilled.type,
        effect: (_, { getState }) => {
          const { activeNode } = getState().cues;
          if (activeNode?.attrs) {
            const hasAllZeroValues = Object.values(activeNode.attrs).every(
              val => val === 0
            );
            if (hasAllZeroValues) return;
          }
          setHasCueChanged(true);
        },
      })
    );

    return () => {
      // @ts-ignore - type issue with addListener + dispatch
      unsubscribe();
    };
  }, [dispatch, setHasCueChanged]);

  const studioClipPreviewContextV2Value = useMemo(
    () => ({
      previews,
      setPreviews,
      hasCueChanged,
      setPreviewToSaved,
      setHasCueChanged,
      resetPreviews,
      addClipToPreviews,
      deleteClipFromPreviews,
    }),
    [deleteClipFromPreviews, hasCueChanged, previews, setPreviewToSaved]
  );

  return (
    <StudioClipContextV2.Provider value={studioClipPreviewContextV2Value}>
      {children}
    </StudioClipContextV2.Provider>
  );
};
